import type { CSSProperties } from 'react';
import {
  PROGRESS_LIST_CARD_BODY_PADDING,
  PROGRESS_LIST_CARD_CALLOUT_BACKGROUND,
  PROGRESS_LIST_CARD_CALLOUT_GAP,
  PROGRESS_LIST_CARD_CALLOUT_PADDING_HORIZONTAL,
  PROGRESS_LIST_CARD_CALLOUT_PADDING_VERTICAL,
  PROGRESS_LIST_CARD_CALLOUT_RADIUS,
  PROGRESS_LIST_CARD_COMPACT_BODY_PADDING,
  PROGRESS_LIST_CARD_COMPACT_CALLOUT_PADDING,
  PROGRESS_LIST_CARD_COMPACT_FOOTER_PADDING_HORIZONTAL,
  PROGRESS_LIST_CARD_COMPACT_FOOTER_PADDING_VERTICAL,
  PROGRESS_LIST_CARD_COMPACT_FOOTER_TEXT_GAP,
  PROGRESS_LIST_CARD_COMPACT_SECTION_GAP,
  PROGRESS_LIST_CARD_FOOTER_GAP,
  PROGRESS_LIST_CARD_FOOTER_PADDING_LEADING,
  PROGRESS_LIST_CARD_FOOTER_PADDING_TRAILING,
  PROGRESS_LIST_CARD_FOOTER_PADDING_VERTICAL,
  PROGRESS_LIST_CARD_FOOTER_RULE_COLOR,
  PROGRESS_LIST_CARD_FOOTER_RULE_WIDTH,
  PROGRESS_LIST_CARD_NEUTRAL_CALLOUT_BACKGROUND,
  PROGRESS_LIST_CARD_NEUTRAL_CALLOUT_RADIUS,
  PROGRESS_LIST_CARD_ROW_GAP,
  PROGRESS_LIST_CARD_SECTION_GAP,
  PROGRESS_LIST_CARD_TEXT_GAP,
} from './ProgressListCard';
const column = (gap: number): CSSProperties => ({ display: 'flex', flexDirection: 'column', gap });
/** Private geometry shared by the loaded card and its decorative sibling. */
export const progressListCardLayout: Record<
  'callout' | 'footer' | 'stack' | 'row' | 'titleBlock' | 'calloutText' | 'footerText',
  CSSProperties
> = {
  callout: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: PROGRESS_LIST_CARD_CALLOUT_GAP,
    backgroundColor: PROGRESS_LIST_CARD_CALLOUT_BACKGROUND,
    borderRadius: PROGRESS_LIST_CARD_CALLOUT_RADIUS,
    paddingTop: PROGRESS_LIST_CARD_CALLOUT_PADDING_VERTICAL,
    paddingBottom: PROGRESS_LIST_CARD_CALLOUT_PADDING_VERTICAL,
    paddingLeft: PROGRESS_LIST_CARD_CALLOUT_PADDING_HORIZONTAL,
    paddingRight: PROGRESS_LIST_CARD_CALLOUT_PADDING_HORIZONTAL,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: PROGRESS_LIST_CARD_FOOTER_GAP,
    paddingTop: PROGRESS_LIST_CARD_FOOTER_PADDING_VERTICAL,
    paddingBottom: PROGRESS_LIST_CARD_FOOTER_PADDING_VERTICAL,
    paddingLeft: PROGRESS_LIST_CARD_FOOTER_PADDING_LEADING,
    paddingRight: PROGRESS_LIST_CARD_FOOTER_PADDING_TRAILING,
    borderTop: `${PROGRESS_LIST_CARD_FOOTER_RULE_WIDTH}px solid ${PROGRESS_LIST_CARD_FOOTER_RULE_COLOR}`,
  },
  stack: { ...column(PROGRESS_LIST_CARD_SECTION_GAP), padding: PROGRESS_LIST_CARD_BODY_PADDING },
  row: column(PROGRESS_LIST_CARD_ROW_GAP),
  titleBlock: column(PROGRESS_LIST_CARD_TEXT_GAP),
  calloutText: { ...column(PROGRESS_LIST_CARD_TEXT_GAP), flex: 1 },
  footerText: { ...column(PROGRESS_LIST_CARD_TEXT_GAP), flex: 1 },
};

/**
 * Overrides for the `compact` density, spread over the default layout only when
 * the card asks for it, so a default card's styles are untouched.
 */
export const progressListCardCompactLayout: Record<
  'callout' | 'footer' | 'stack' | 'footerText',
  CSSProperties
> = {
  stack: {
    gap: PROGRESS_LIST_CARD_COMPACT_SECTION_GAP,
    padding: PROGRESS_LIST_CARD_COMPACT_BODY_PADDING,
  },
  callout: {
    paddingTop: PROGRESS_LIST_CARD_COMPACT_CALLOUT_PADDING,
    paddingBottom: PROGRESS_LIST_CARD_COMPACT_CALLOUT_PADDING,
    paddingLeft: PROGRESS_LIST_CARD_COMPACT_CALLOUT_PADDING,
    paddingRight: PROGRESS_LIST_CARD_COMPACT_CALLOUT_PADDING,
  },
  footer: {
    paddingTop: PROGRESS_LIST_CARD_COMPACT_FOOTER_PADDING_VERTICAL,
    paddingBottom: PROGRESS_LIST_CARD_COMPACT_FOOTER_PADDING_VERTICAL,
    paddingLeft: PROGRESS_LIST_CARD_COMPACT_FOOTER_PADDING_HORIZONTAL,
    paddingRight: PROGRESS_LIST_CARD_COMPACT_FOOTER_PADDING_HORIZONTAL,
  },
  footerText: { gap: PROGRESS_LIST_CARD_COMPACT_FOOTER_TEXT_GAP },
};

/** Override for the `neutral` callout tone, spread only when the card asks for it. */
export const progressListCardNeutralCallout: CSSProperties = {
  backgroundColor: PROGRESS_LIST_CARD_NEUTRAL_CALLOUT_BACKGROUND,
  borderRadius: PROGRESS_LIST_CARD_NEUTRAL_CALLOUT_RADIUS,
};
